import axios, { isAxiosError } from 'axios';
import qs from 'qs';

import { filterEmptyParams, relativeUrlResolver } from '@/services/utils';

import type { DamSelection } from '../utils/useDamSelection';

const ASSETS_URL = '/api/v2/assets/';

/** Per-asset bulk calls run for at most this many assets until the bulk endpoint exists (LAX-10482, D-037). */
const DAM_BULK_ACTION_LIMIT = 100;

/** Search part of the library query, used to resolve an "all matching" selection. */
interface DamSelectionQuery {
    keyword?: string;
    filter?: string[];
    sort?: string[];
    direction?: string[];
}

type DamBulkFailureReason = 'forbidden' | 'notFound' | 'conflict' | 'unknown';

/** An asset a bulk action could not handle; the reason comes from the HTTP status of its call. */
interface DamBulkFailure {
    primaryKey: string;
    reason: DamBulkFailureReason;
    filename?: string;
}

interface DamBulkResult {
    succeeded: string[];
    failed: DamBulkFailure[];
}

const failureReasonOf = (error: unknown): DamBulkFailureReason => {
    const status = isAxiosError(error) ? error.response?.status : undefined;
    switch (status) {
        case 403:
            return 'forbidden';
        case 404:
            return 'notFound';
        case 409:
            return 'conflict';
        default:
            return 'unknown';
    }
};

/** Adds the file names of the failed assets that still exist; the others keep their key only. */
const withFilenames = async (failures: DamBulkFailure[]): Promise<DamBulkFailure[]> => {
    if (failures.length === 0) {
        return failures;
    }
    try {
        const response = await axios.get<Array<{ primaryKey__: string; filename__?: string }>>(relativeUrlResolver(ASSETS_URL), {
            params: { items: failures.map((failure) => failure.primaryKey), fields: ['primaryKey__', 'filename__'], count: failures.length },
            paramsSerializer: (params) => qs.stringify(filterEmptyParams(params), { arrayFormat: 'repeat' }),
        });
        const names = new Map((response.data ?? []).map((record) => [String(record.primaryKey__), record.filename__]));
        return failures.map((failure) => ({ ...failure, filename: names.get(failure.primaryKey) ?? failure.filename }));
    } catch {
        return failures;
    }
};

/** Primary keys of the selection, at most `limit`; "all matching" pages through the search with `fields=primaryKey__`. */
const resolveSelectionKeys = async (selection: DamSelection, query: DamSelectionQuery, limit = DAM_BULK_ACTION_LIMIT): Promise<string[]> => {
    if (selection.mode === 'ids') {
        return [...selection.ids].slice(0, limit);
    }
    const response = await axios.get<Array<{ primaryKey__: string }>>(relativeUrlResolver(ASSETS_URL), {
        params: { ...query, fields: ['primaryKey__'], count: limit + selection.excludedIds.size },
        paramsSerializer: (params) => qs.stringify(filterEmptyParams(params), { arrayFormat: 'repeat' }),
    });
    return (response.data ?? [])
        .map((record) => String(record.primaryKey__))
        .filter((key) => !selection.excludedIds.has(key))
        .slice(0, limit);
};

/** Deletes one asset after the other; failures do not stop the run. */
const deleteAssets = async (keys: string[], onProgress?: (done: number) => void): Promise<DamBulkResult> => {
    const result: DamBulkResult = { succeeded: [], failed: [] };
    for (const key of keys) {
        try {
            await axios.delete(relativeUrlResolver(`${ASSETS_URL}${encodeURIComponent(key)}`));
            result.succeeded.push(key);
        } catch (error) {
            result.failed.push({ primaryKey: key, reason: failureReasonOf(error) });
        }
        onProgress?.(result.succeeded.length + result.failed.length);
    }
    return result;
};

export { DAM_BULK_ACTION_LIMIT, deleteAssets, failureReasonOf, resolveSelectionKeys, withFilenames };
export type { DamBulkFailure, DamBulkFailureReason, DamBulkResult, DamSelectionQuery };
